import * as React from 'react';
import { Flex } from '@/hen-ui/composites/Flex';
import { cn } from '@/hen-ui/utils';
import {
  ExpertByLine,
  type ExpertByLineProps,
} from '@/hen-ui/composites/ExpertByLine';
import { Link, Text, textVariants } from '@/hen-ui/atomics';
import { SectionHeader } from '@/hen-ui/composites/SectionHeader';
import {
  expertBoxVariants,
  type ExpertBoxVariantProps,
} from './expert-box.variants';

export interface ExpertBoxProps
  extends ExpertBoxVariantProps,
    React.HTMLAttributes<HTMLDivElement> {
  actionLabel?: string;
  actionHref?: string;
  brow?: string;
  experts: ExpertByLineProps[];
  title: string;
  question?: string;
  source?: {
    label: string;
    href: string;
  };
  content?: string;
  children?: React.ReactNode;
}

/**
 * ExpertBox - A Flex container with rounded corners and neutral-50 background.
 * Use for expert/content callout sections.
 */
export const ExpertBox = React.forwardRef<HTMLDivElement, ExpertBoxProps>(
  (
    {
      actionLabel,
      actionHref,
      brow,
      experts,
      title,
      question,
      source,
      children,
      content,
      variant,
      className,
      ...props
    },
    ref,
  ) => (
    <Flex
      ref={ref}
      className={cn(expertBoxVariants({ variant }), className)}
      {...props}
    >
      <SectionHeader
        size='h3'
        title={title}
        brow={brow ?? undefined}
        actionButton={
          actionLabel && actionHref
            ? { text: actionLabel, href: actionHref }
            : undefined
        }
        subtext={question ?? undefined}
      />
      <Flex
        direction='row'
        gap='xl'
        className='min-w-0 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
        role='list'
      >
        {experts.map((expert, index) => (
          <Flex
            key={`${expert.name}-${expert.role}-${index}`}
            className='shrink-0'
            role='listitem'
            direction='row'
          >
            <ExpertByLine {...expert} />
          </Flex>
        ))}
      </Flex>
      {content && (
        <Text variant='b3' color='default'>
          {content}
        </Text>
      )}
      {children && (
        <Flex
          gap='none'
          direction='column'
          className={cn(textVariants({ variant: 'b3', color: 'default' }))}
        >
          {children}
        </Flex>
      )}
      {source && (
        <Text variant='l1' color='default' tag='span'>
          Source:{' '}
          <Link
            underline='always'
            variant='primary'
            href={source.href}
            className='text-l1'
          >
            {source.label}
          </Link>
        </Text>
      )}
    </Flex>
  ),
);

ExpertBox.displayName = 'ExpertBox';
